import React, { useContext } from 'react';
import {
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Button,
    Typography,
    Box,
    Divider,
    Avatar,
    Chip,
    Grid,
    Hidden,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@material-ui/core';
import SunEditor from 'suneditor-react';
import { ProgressBar } from "react-step-progress-bar";
import cx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { useOverShadowStyles } from '@mui-treasury/styles/shadow/over';
import { pink } from '@material-ui/core/colors';
import TextInfoContent from '@mui-treasury/components/content/textInfo';
import { useN04TextInfoContentStyles } from '@mui-treasury/styles/textInfoContent/n04';
import { Coin } from '../../shared/component';
import useStyles from '../../../styles/components/rewards/stylesRewardShop';
import StudentContext from '../../../context/student-context';
import RewardContext from '../../../context/reward-context';
import { ConvertDateString } from '../../../utils/helpers/dateformat/ConvertDateString'
import { useHistory } from "react-router-dom";
import { getStudentCoins } from '../../../actions/share-actions';
import { submitDonationRequest, getTransactionLogsByStudentNumber, getCampaigns } from '../../../actions/reward-actions';

const useCustomStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 650,
        margin: 'auto',
        marginBottom: 25,
        borderRadius: 12,
        padding: 12,
        width: 650
    },
    media: {
        borderRadius: 6,
    },
    avatarGroup: {
        marginBottom: 20,
        fontFamily: 'Roboto',
    },
    avatar: {
        border: '1px solid gray'
    },
    amountSection: {
        margin: '20px 0 20px 0'
    },
    donateSection: {
        margin: '20px 0 0 0'
    },
    chip: {
        marginRight: 15,
        marginTop: 10,
        '&:hover': {
            transform: 'scale(1.1)',
        },
    },
    pink: {
        color: pink[500],
    },
}));

export default function Campaign() {

    const { rewardContext, dispatch, inputSnackBarDetails } = useContext(RewardContext);
    const { dispatchStudent } = useContext(StudentContext);
    const history = useHistory();
    const classes = useStyles()
    const styles = useCustomStyles();
    const shadowStyles = useOverShadowStyles({ inactive: true });
    const textCardContentStyles = useN04TextInfoContentStyles();
    const [amount, SetAmount] = React.useState(0);
    const [open, setOpen] = React.useState(false);
    const [donateViewModel, setDonateViewModel] = React.useState(
        { campaignId: 0, campaignName: '', amount: '', targetAmount: '', coinExchangeRate: 0 });

    const handleClick = (value) => {
        SetAmount(value);
    };

    const initCampaigns = async () => {
        const response = await getCampaigns();
        if (response > 200 || response.status === "Failed") {
            return history.push("/SessionTimeOut")
        }

        dispatch({ type: 'SET_CAMPAIGNS', campaigns: response.data });
    };

    const initTransactionLogsByStudentNumber = async () => {
        const response = await getTransactionLogsByStudentNumber()
        if (response > 200 || response.status === "Failed") {
            return history.push("/SessionTimeOut")
        }

        const rewardRedemptionLog = response.data
        dispatch({ type: 'SET_REWARD_REDEMPTION_LOG', rewardRedemptionLog });
        setIsLoading(false);
    }

    const handleClose = () => setOpen(false);

    const handleDonate = async (campaign) => {
        if (amount < 1) {
            inputSnackBarDetails('Please select donation amount', 'error');
        }
        else {
            setOpen(true);
            setDonateViewModel({
                campaignId: campaign.campaignId,
                campaignName: campaign.name,
                amount: amount,
                targetAmount: campaign.targetAmount,
                coinExchangeRate: campaign.coinExchangeRate
            })
        }
    }

    const handleOk = async () => {
        handleClose();
        dispatch({ type: 'SET_LOADING', loading: true });

        let response = await submitDonationRequest(JSON.stringify(donateViewModel));
        if (response > 200) {
            return history.push("/SessionTimeOut")
        }

        if (response.status === "Success") {
            dispatch({ type: 'SET_LOADING', loading: false });

            SetAmount(0)
            initTransactionLogsByStudentNumber();
            initCampaigns();
            // update coin for Drawer Header
            const responseCoin = await getStudentCoins();
            dispatchStudent({ type: 'SET_STUDENT_COINS', studentCoins: responseCoin.data });

            inputSnackBarDetails(`You\'ve successfully contributed ${donateViewModel.amount} SGD to ${donateViewModel.campaignName}. Thank you for your contribution.`, 'success');
        }
        else {
            SetAmount(0)
            dispatch({ type: 'SET_LOADING', loading: false });
            inputSnackBarDetails(`Donation Failed. ( ${response.message} ) Please email to Adventurelearn@singaporetech.edu.sg if you have any enquiries.`, 'error');
        }
    }
    return (
        <>
            {
                rewardContext.campaigns.length > 0 ?
                    rewardContext.campaigns.map((campaign, index) => (
                        <Card key={index} className={cx(styles.root, shadowStyles.root)}>
                            <CardContent>
                                <TextInfoContent
                                    classes={textCardContentStyles}
                                    overline={'Donation'}
                                    heading={campaign.name}
                                    body={
                                        <SunEditor
                                            showToolbar={false}
                                            enableToolbar={true}
                                            disable={true}
                                            setOptions={{
                                                height: "100%",
                                                resizingBar: false,
                                            }}
                                            toggleFullScreen={true}
                                            setContents={campaign.description}
                                            showController={false}
                                        />
                                    }
                                />
                                <Box sx={{ width: '100%', maxWidth: 500 }}>
                                    <Typography variant="caption" display="block"
                                        gutterBottom style={{ fontSize: '14px' }}>
                                        <span className={styles.pink}>{campaign.currentAmount}$ raised</span> of {campaign.targetAmount}$
                                    </Typography>
                                    <ProgressBar
                                        percent={campaign.currentAmount * 100 / campaign.targetAmount}
                                    />
                                    <Typography variant="caption" display="block" gutterBottom style={{ margin: '10px 0px 15px 0px', fontSize: '14px' }}>
                                        <span className={styles.pink}>Campaign Expiry Date is </span>
                                        {ConvertDateString.convertForDatePicker(campaign.endDate)}
                                    </Typography>
                                    {
                                        campaign.numOfDonor > 1 ?
                                            <Typography variant="caption" display="block" gutterBottom style={{ fontSize: '14px' }}>
                                                <span className={styles.pink}>+{campaign.numOfDonor} students </span>donated
                                            </Typography>
                                            : <></>
                                    }
                                </Box>
                                <Divider variant="middle" />
                                <Box sx={{ width: '100%', maxWidth: 500 }} className={styles.amountSection}>
                                    <Typography gutterBottom variant="body1" >
                                        Select Amount
                                    </Typography>
                                    <div>
                                        {new Array(10).fill(0).map((_, index) => (
                                            <Chip key={index} label={`${index + 1} $`} color="secondary" variant={amount === index + 1 ? 'default' : 'outlined'}
                                                onClick={() => handleClick(index + 1)} className={styles.chip} />
                                        ))}
                                    </div>
                                    <Typography variant="overline" display="block" gutterBottom style={{ fontSize: '14px' }}>
                                        <span className={styles.pink}>1 SGD =</span> {campaign.coinExchangeRate} Coins
                                    </Typography>
                                </Box>
                                <Divider variant="middle" />
                                <Grid container justify="center" className={styles.donateSection}>
                                    <Grid
                                        onClick={() => handleDonate(campaign)}
                                        classes={{ root: classes.containerButtonRedeem }}
                                        container
                                        justify="center"
                                        alignContent="center"
                                    >
                                        <Grid
                                            classes={{ root: classes.containerButtonContent }}
                                            item xs={6}
                                            container
                                            justify="center"
                                            alignContent="center"
                                        >
                                            Donate
                                        </Grid>
                                        <Grid
                                            classes={{ root: classes.containerButtonCoin }}
                                            item xs={6}
                                            container
                                            justify="center"
                                            alignContent="center"
                                        >
                                            <Hidden smDown>
                                                <Coin
                                                    size="medium"
                                                    coin={amount * campaign.coinExchangeRate}
                                                    color={'non-border'}
                                                />
                                            </Hidden>
                                            <Hidden mdUp>
                                                <Coin
                                                    size="small"
                                                    coin={amount * campaign.coinExchangeRate}
                                                    color={'non-border'}
                                                />
                                            </Hidden>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))
                    :
                    <Grid container justify="center">
                        <div>Please watch out this space for donation campaign.</div>
                    </Grid>
            }
            <Dialog
                open={open}
                classes={{
                    root: classes.rootDialogConfirm,
                    paper: classes.paperDialogConfirm,
                }}
            >
                <DialogTitle disableTypography={true} classes={{ root: classes.rootDialogConfirmTitle }}>
                    Donation Confirmation
                </DialogTitle>
                <DialogContent classes={{ root: classes.rootDialogConfirmContent }}>
                    <div className={classes.dialogContentConfirm}>You want to doante {amount}$.</div>
                    <div className={classes.dialogContentConfirm}>Please kindly confirm 'Yes' or 'No'</div>
                </DialogContent>
                <DialogActions classes={{ root: classes.rootDialogActions }}>
                    <Button onClick={handleClose} className={cx(classes.buttonDialogConfirm, classes.buttonCancle)}>
                        No
                    </Button>
                    <Button onClick={handleOk} className={cx(classes.buttonDialogConfirm, classes.buttonYes)}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

//<CardMedia
//    component="img"
//    height="150"
//    alt="Paella dish"
//    className={cx(styles.media)}
//    image={
//        '/assets/images/components/reward/donate.png'
//    }
///>